/**
 * Theme Store
 *
 * Manages application theme state including:
 * - Light/dark mode preference
 * - System preference detection
 * - Theme persistence to localStorage
 *
 * @module themeStore
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ==================== Types ====================

export type ColorScheme = 'light' | 'dark' | 'auto';

interface ThemeState {
  /** Current color scheme preference */
  colorScheme: ColorScheme;
  /** Resolved theme (actual theme being used) */
  resolvedTheme: 'light' | 'dark';
  /** Set color scheme preference */
  setColorScheme: (scheme: ColorScheme) => void;
  /** Update resolved theme based on preference and system settings */
  updateResolvedTheme: () => void;
  /** Toggle between light and dark mode */
  toggleTheme: () => void;
}

// ==================== Helper Functions ====================

/**
 * Get system color scheme preference
 */
function getSystemTheme(): 'light' | 'dark' {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

/**
 * Resolve the actual theme based on preference
 */
function resolveTheme(colorScheme: ColorScheme): 'light' | 'dark' {
  if (colorScheme === 'auto') {
    return getSystemTheme();
  }
  return colorScheme;
}

// ==================== Store ====================

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      colorScheme: 'light',
      resolvedTheme: 'light',

      setColorScheme: (scheme: ColorScheme) => {
        const resolvedTheme = resolveTheme(scheme);
        set({ colorScheme: scheme, resolvedTheme });

        // Update document class for Tailwind dark mode
        if (resolvedTheme === 'dark') {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      },

      updateResolvedTheme: () => {
        const { colorScheme } = get();
        const resolvedTheme = resolveTheme(colorScheme);
        set({ resolvedTheme });

        // Update document class for Tailwind dark mode
        if (resolvedTheme === 'dark') {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      },

      toggleTheme: () => {
        const { resolvedTheme } = get();
        const newScheme = resolvedTheme === 'dark' ? 'light' : 'dark';
        get().setColorScheme(newScheme);
      },
    }),
    {
      name: 'ai-novel-theme',
      partialize: (state) => ({ colorScheme: state.colorScheme }),
    }
  )
);

// ==================== Theme Initialization ====================

/**
 * Initialize theme system
 * Should be called once at app startup
 */
export function initializeTheme(): void {
  const { colorScheme, setColorScheme, updateResolvedTheme } = useThemeStore.getState();

  // Apply initial theme
  setColorScheme(colorScheme);

  // Listen for system theme changes
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', () => {
      updateResolvedTheme();
    });
  }
}

export default useThemeStore;
